import { v4 as uuidv4 } from "uuid";
import { safeLocalStorage } from "../../utils/safeStorage";

const SESSION_ID_KEY = "sessionId";
const SESSION_COOKIE_NAME = "ll_session_id";
const SESSION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const SESSION_ID_PATTERN = /^[a-zA-Z0-9_-]{8,160}$/;

let memorySessionId = "";

const normalizeSessionId = (value = "") => {
  const text = String(value || "").trim();
  return SESSION_ID_PATTERN.test(text) ? text : "";
};

const getBrowserSessionStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage || null;
  } catch (_error) {
    return null;
  }
};

const getBrowserLocalStorage = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage || null;
  } catch (_error) {
    return null;
  }
};

const getBrowserDocument = () => {
  if (typeof document === "undefined") return null;
  return document;
};

const getStorageValue = (storage, key) => {
  try {
    return normalizeSessionId(storage?.getItem?.(key));
  } catch (_error) {
    return "";
  }
};

const setStorageValue = (storage, key, value) => {
  try {
    storage?.setItem?.(key, value);
    return getStorageValue(storage, key) === value;
  } catch (_error) {
    return false;
  }
};

const removeStorageValue = (storage, key) => {
  try {
    storage?.removeItem?.(key);
  } catch (_error) {
    // Storage cleanup should not block session continuity.
  }
};

const isMemoryBackedStorage = (storage) =>
  storage?.persistent === false || storage?.type === "memory";

const isPersistentStorageName = (name = "") =>
  !["memory", "safeLocalStorageMemory"].includes(name);

const readCookieValue = (name, documentObject = getBrowserDocument()) => {
  if (!documentObject || typeof documentObject.cookie !== "string") return "";
  const encodedName = encodeURIComponent(name);
  const match = documentObject.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${encodedName}=`));
  if (!match) return "";

  try {
    return normalizeSessionId(decodeURIComponent(match.slice(encodedName.length + 1)));
  } catch (_error) {
    return "";
  }
};

const writeCookieValue = (
  name,
  value,
  documentObject = getBrowserDocument(),
) => {
  if (!documentObject) return false;
  try {
    const secure =
      typeof window !== "undefined" && window.location?.protocol === "https:"
        ? "; Secure"
        : "";
    documentObject.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
      value,
    )}; Max-Age=${SESSION_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
    return readCookieValue(name, documentObject) === value;
  } catch (_error) {
    return false;
  }
};

const storageSources = ({
  localStorage = getBrowserLocalStorage(),
  sessionStorage = getBrowserSessionStorage(),
  documentObject = getBrowserDocument(),
  fallbackStorage = safeLocalStorage,
} = {}) => {
  const memoryFallbackStorage = isMemoryBackedStorage(localStorage)
    ? localStorage
    : isMemoryBackedStorage(fallbackStorage)
      ? fallbackStorage
      : null;
  const localStorageSource = {
    name: "localStorage",
    read: () => getStorageValue(localStorage, SESSION_ID_KEY),
    write: (value) => setStorageValue(localStorage, SESSION_ID_KEY, value),
    remove: () => removeStorageValue(localStorage, SESSION_ID_KEY),
  };
  const memorySafeLocalStorageSource = {
    name: "safeLocalStorageMemory",
    read: () => getStorageValue(memoryFallbackStorage, SESSION_ID_KEY),
    write: (value) =>
      setStorageValue(memoryFallbackStorage, SESSION_ID_KEY, value),
    remove: () => removeStorageValue(memoryFallbackStorage, SESSION_ID_KEY),
  };
  const sessionStorageSource = {
    name: "sessionStorage",
    read: () => getStorageValue(sessionStorage, SESSION_ID_KEY),
    write: (value) => setStorageValue(sessionStorage, SESSION_ID_KEY, value),
    remove: () => removeStorageValue(sessionStorage, SESSION_ID_KEY),
  };

  return [
    ...(isMemoryBackedStorage(localStorage) ? [] : [localStorageSource]),
    sessionStorageSource,
    {
      name: "cookie",
      read: () => readCookieValue(SESSION_COOKIE_NAME, documentObject),
      write: (value) =>
        writeCookieValue(SESSION_COOKIE_NAME, value, documentObject),
      remove: () => {},
    },
    ...(memoryFallbackStorage ? [memorySafeLocalStorageSource] : []),
    {
      name: "memory",
      read: () => normalizeSessionId(memorySessionId),
      write: (value) => {
        memorySessionId = normalizeSessionId(value);
        return Boolean(memorySessionId);
      },
      remove: () => {
        memorySessionId = "";
      },
    },
  ];
};

export const readStoredSessionId = (options = {}) => {
  for (const source of storageSources(options)) {
    const value = source.read();
    if (value) return value;
  }
  return "";
};

export const persistSessionId = (sessionId, options = {}) => {
  const normalized = normalizeSessionId(sessionId);
  if (!normalized) return { persisted: false, primaryStorage: "none" };

  let primaryStorage = "memory";
  let persisted = false;
  storageSources(options).forEach((source) => {
    const wrote = source.write(normalized);
    if (wrote && !persisted) {
      primaryStorage = source.name;
      persisted = true;
    }
  });

  return { persisted, primaryStorage };
};

export const getOrCreateSessionIdentity = ({
  createId = uuidv4,
  ...options
} = {}) => {
  const existing = readStoredSessionId(options);
  if (existing) {
    const persistence = persistSessionId(existing, options);
    return {
      sessionId: existing,
      created: false,
      primaryStorage: persistence.primaryStorage,
      persistent: isPersistentStorageName(persistence.primaryStorage),
    };
  }

  const sessionId = normalizeSessionId(createId());
  if (!sessionId) {
    return {
      sessionId: "",
      created: false,
      primaryStorage: "none",
      persistent: false,
    };
  }

  const persistence = persistSessionId(sessionId, options);
  return {
    sessionId,
    created: true,
    primaryStorage: persistence.primaryStorage,
    persistent: isPersistentStorageName(persistence.primaryStorage),
  };
};

export const getOrCreateSessionId = (options = {}) =>
  getOrCreateSessionIdentity(options).sessionId;

export const getCurrentSessionId = (preferredSessionId = "", options = {}) =>
  normalizeSessionId(preferredSessionId) || readStoredSessionId(options) || null;

export const clearSessionIdentityForTests = () => {
  memorySessionId = "";
};

export const _private = {
  normalizeSessionId,
  readCookieValue,
  writeCookieValue,
  isMemoryBackedStorage,
};
